/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import ItemsSelectionMode from '@/components/batches/ItemsSelectionMode'
import UploadCsvForm from '@/components/batches/UploadCsvForm'
import { aiMetadataBatchesUrls } from '@/configs/urls'
import { getItemPropByTable, setItemCustomProps, tableColumns } from './handlers'
import {
  AI_METADATA_BATCH_CSV_TITLE,
  BACK_TO_AI_METADATA_LABEL,
  CREATE_AI_METADATA_BATCH,
  CREATE_AI_METADATA_BATCH_DESCRIPTION
} from '@/configs/strings/ai-metadata-batches.strings'
import { getLicensedAssets } from '@/services/licensed-assets'
import { registerAIMetadataBuildBatch, registerCSVAIMetadataBuildBatch } from '@/services/ai-metadata-build-batches'
import { filterOptions, sortOptions } from '@/pages/LicensedAssets/pages/LicensedAssetsList/handlers'
import { useNavigate } from 'react-router-dom'
import ItemBatchCreator from '@/components/forms/predefined/ItemBatchCreator'
import HorizontalCardWithAddRemoveOptions from '@/components/cards/HorizontalCardWithAddRemoveOptions'
import { defaultToastConfig } from '@/configs/toast'
import { useToast } from '@chakra-ui/react'

const CreateAIMetadataBatch = () => {
  const [uploadingFile, setUploadingFile] = useState(false)
  const toast = useToast()
  const [itemsSelectionMode, setItemsSelectionMode] = useState('uploadCsv')
  const isUploadCsv = itemsSelectionMode === 'uploadCsv'
  const navigate = useNavigate()
  const onSubmitCSV = async (formData, getAccessToken) => {
    setUploadingFile(true)
    return await registerCSVAIMetadataBuildBatch(formData, getAccessToken)
  }
  const uploadFileResponse = async (response) => {
    try {
      toast({
        title: AI_METADATA_BATCH_CSV_TITLE,
        description: `The ${AI_METADATA_BATCH_CSV_TITLE} '${response.name}' is being processed; we will notify you when it is finished.`,
        status: 'success',
        ...defaultToastConfig
      })

      if (response?.id) {
        setUploadingFile(false)

        navigate(`/${aiMetadataBatchesUrls.base}/${response.id}`, { replace: true })
      }
    } catch (error) {
      toast({
        title: `Error on ${AI_METADATA_BATCH_CSV_TITLE}`,
        description: error.response?.data?.message ||
          `Failed processing the ${AI_METADATA_BATCH_CSV_TITLE}. Please contact with us.`,
        status: 'error',
        ...defaultToastConfig
      })
    }
  }

  return (
    <RegularTemplate
      backUrl={`/${aiMetadataBatchesUrls.base}`}
      backLabel={BACK_TO_AI_METADATA_LABEL}
      title={CREATE_AI_METADATA_BATCH}
      description={CREATE_AI_METADATA_BATCH_DESCRIPTION}
      loadingContent={uploadingFile}
    >
      <ItemsSelectionMode
        className="mb-5 inline-block"
        onChange={setItemsSelectionMode}
      />
      { isUploadCsv
        ? <UploadCsvForm
            csvService={onSubmitCSV}
            getResponse={uploadFileResponse}
          />
        : <ItemBatchCreator
            baseUrl={aiMetadataBatchesUrls.base}
            filterOptions={filterOptions}
            getItemPropByTable={getItemPropByTable}
            getItemsService={getLicensedAssets}
            itemComponent={HorizontalCardWithAddRemoveOptions}
            itemsKey="assets"
            registerBatchService={registerAIMetadataBuildBatch}
            searchResultsKeyData="data"
            searchResultsKeyLabel="name"
            searchResultsKeyValue="name"
            setItemCustomProps={setItemCustomProps}
            sortOptions={sortOptions}
            tableColumns={tableColumns}
          />
      }
    </RegularTemplate>
  )
}

export default CreateAIMetadataBatch
